<template>
  <b-container fluid class="contents">
    <b-row>
      <b-col class="d-flex align-items-center">
        <span class="contents-title mb-3">Categories</span>
        <i class="guide-tooltip position-relative" id="contents-tooltip" style="top: -6px"></i>
        <b-popover
          target="contents-tooltip"
          triggers="hover"
          content="Sort your products according to Categories to make it easy for your customers to browse your products. Select up to 5 categories to feature on your homepage."
        ></b-popover>
        <switches v-model="form.categories_enable"
          theme="custom"
          color="primary"
          type-bold="true"
          style="top: 8px; left: 10px;"
          @input="handleChanges(null)"
          :emit-on-mount="false">
        </switches>
      </b-col>
    </b-row>
    <div v-if="isLoading" class="d-flex align-items-center justify-content-center min-height-300px">
      Loading...
    </div>
    <template v-else>
      <b-row v-for="(category, index) in form.categories" :key="`category_${index}`" class="border-bottom mb-3">
        <b-col md="12">
          <b-form-group label="Add Category" label-class="d-flex justify-content-between align-items-center">
            <template slot="label">
              Add Category
              <b-button variant="ghost" @click="removeCategory(index)" v-if="form.categories.length > 1">
                <img src="@/assets/images/u_trash-alt.svg" alt="Delete" class="img-fluid">
              </b-button>
            </template>
            <b-row>
              <b-col md="4">
                <b-form-select v-model="category.uuid" :class="{ 'text-muted': isEstoreSettingsLoading || !category.uuid }"
                  @change="handleChanges({ formKey: 'categories', index: index, uuid: category.uuid })">
                  <option value="null" disabled>Select</option>
                  <option v-for="(categoryOption, categoryOptionIndex) in categoryOptions"
                    :key="`categoryOption_${categoryOptionIndex}`" :value="categoryOption.value"
                    :disabled="selectedCategories.includes(categoryOption.value)">
                    {{ categoryOption.text }}
                  </option>
                </b-form-select>
                <b-form-invalid-feedback :force-show="!isInvalid(`categories.${index}.name`)">{{ errorMessage(`categories.${index}.name`)  }}</b-form-invalid-feedback>
              </b-col>
            </b-row>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            label="Background Color"
            label-for="background-color"
            class="mb-0"
            required
          >
            <input
              type="color"
              id="background-color"
              class="color-picker p-0 rounded"
              v-model="category.background_color"
              required
              @change="handleChanges(null)"
            />
            <img
              style="margin-top: -35px; margin-left: 5px"
              src="../../../assets/images/icons/colorpicker.svg"
              alt="Color Picker"
            />
          </b-form-group>
        </b-col>
        <b-col md="12" class="mb-3">
          <small style="color: #979797; font-size: 11px;">{{ category.background_color }}</small>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            :variant="form.categories.length >= 5 ? 'disabled' : 'ghost'"
            class="text-primary p-0 shadow-none"
            @click="addCategory"
            :disabled="form.categories.length >= 5">
            <img 
              class="img-fluid"
              src="@/assets/images/icons/add.svg"
              alt="Add Icon"
              height="26"
              width="26"
            > Add New Category
          </b-button>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Switches from "vue-switches";
import notification from "@/services/notification";

export default {
  name: "designAdvancedSettingsCategories",
  components: { Switches },
  data() {
    return {
      form: {
        categories_enable: true,
        categories: [
          {
            uuid: null,
            name: null,
            background_color: "#FF888"
          },
          {
            uuid: null,
            name: null,
            background_color: "#FF888"
          }
        ]
      }
    }
  },
  computed: {
    ...mapGetters({
      estoreSettings: "estoreSettings/settings",
      isLoading: "designs/loading",
      isEstoreSettingsLoading: "estoreSettings/loading",
      categories: "designs/categories",
      categoriesErrors: "designs/errors"
    }),
    selectedCategories: {
      get() {
        return this.form.categories.map(category => category.uuid)
      },
      set(categories) {
        return categories;
      }
    },
    categoryOptions: {
      get() {
        return !this.isEstoreSettingsLoading && this.estoreSettings ?
          this.estoreSettings.categories.map(category => {
            return {
              value: category.uuid,
              text: category.name,
              id: category.id
            }
          })
          : []
      },
      set(categories) {
        return categories;
      }
    }
  },
  mounted() {
    this.$root.$on('handleSave', (routeName) => {
      if (routeName === 'designAdvancedSettingsCategories') this.saveCategories();
    });
    this.$root.$on('saveChangesCancelled', (value) => {
      if (value) this.form = JSON.parse(JSON.stringify(this.categories));
      this.setErrors(null);
    });
  },
  beforeDestroy() {
    this.$root.$off('handleSave');
    this.$root.$off('saveChangesCancelled');
  },
  async created() {
    this.viewEstoreSettings();
    await this.getAdvancedSettingsCategories();
    this.form = JSON.parse(JSON.stringify(this.categories));
  },
  methods: {
    ...mapActions({
      viewEstoreSettings: "estoreSettings/viewSettings",
      getAdvancedSettingsCategories: "designs/getAdvancedSettingsCategories",
      saveAdvancedSettingsCategories: "designs/saveAdvancedSettingsCategories"
    }),
    ...mapMutations({
      setErrors: "designs/errors"
    }),
    async saveCategories() {
      this.setErrors(null);

      const errors = {};

      this.form.categories.map((category, index) => {
        if (!category.uuid) {
          errors[`categories.${index}.uuid`] = "Uuid is required.";
          errors[`categories.${index}.name`] = "Name is required.";
          notification.warning("Oops, something went wrong.");
        }
      });

      this.setErrors(errors);

      if (Object.keys(errors).length === 0) {
        await this.saveAdvancedSettingsCategories(this.form);
        if (this.categoriesErrors && Object.keys(this.categoriesErrors).length === 0) this.$root.$emit('showSaveAlert', false);
      }
    },
    removeCategory(index) {
      this.form.categories.splice(index, 1);
      this.$root.$emit('showSaveAlert', true);
    },
    addCategory() {
      this.form.categories.push({ uuid: null, name: null, background_color: "#FF8828" });
      this.$root.$emit('showSaveAlert', true);
    },
    handleChanges(params) {
      if (params)  {
        this.form[params.formKey][params.index]['name'] = this.categoryOptions.find(category => category.value == params.uuid).text; 
        this.form[params.formKey][params.index]['id'] = this.categoryOptions.find(category => category.value == params.uuid).id;
      }
      this.$root.$emit('showSaveAlert', true);
    },
    isInvalid(field) {
      return this.categoriesErrors && Object.keys(this.categoriesErrors).includes(field) ? false : null;
    },
    errorMessage(field) {
      return this.categoriesErrors && field in this.categoriesErrors
        ? this.categoriesErrors[field]
        : null;
    }
  },
};
</script>

<style lang="scss" scoped>
.color-picker {
  height: 50px;
  width: 100px;
  background: none;
  border: none;
}
.min-height-300px {
  min-height: 300px;
}
</style>